'use client';

import { useMemo } from 'react';
import {
  Monitor,
  Smartphone,
  AlertCircle,
  AlertTriangle,
  Info,
  CheckCircle,
} from 'lucide-react';
import type { AuditFinding, SeverityLevel } from '@/types/audit';
import styles from './CompareView.module.css';

interface CompareViewProps {
  desktopFindings: AuditFinding[];
  mobileFindings: AuditFinding[];
  desktopScreenshot?: string;
  mobileScreenshot?: string;
  locale?: string;
  onFindingClick?: (finding: AuditFinding, device: 'desktop' | 'mobile') => void;
}

// Severity counts type
interface SeverityCounts {
  critical: number;
  high: number;
  medium: number;
  low: number;
  total: number;
}

function getSeverityIcon(severity: SeverityLevel, size = 14) {
  switch (severity) {
    case 'critical':
      return <AlertCircle size={size} />;
    case 'high':
      return <AlertTriangle size={size} />;
    case 'medium':
      return <Info size={size} />;
    default:
      return <CheckCircle size={size} />;
  }
}

function countSeverities(findings: AuditFinding[]): SeverityCounts {
  return findings.reduce(
    (acc, f) => {
      acc[f.severity]++;
      acc.total++;
      return acc;
    },
    { critical: 0, high: 0, medium: 0, low: 0, total: 0 }
  );
}

export function CompareView({
  desktopFindings,
  mobileFindings,
  desktopScreenshot,
  mobileScreenshot,
  locale = 'en',
  onFindingClick,
}: CompareViewProps) {
  const desktopCounts = useMemo(() => countSeverities(desktopFindings), [desktopFindings]);
  const mobileCounts = useMemo(() => countSeverities(mobileFindings), [mobileFindings]);

  const i18n = {
    en: {
      desktop: 'Desktop',
      mobile: 'Mobile',
      issues: 'issues',
      critical: 'Critical',
      high: 'High',
      medium: 'Medium',
      low: 'Low',
      noIssues: 'No issues found',
      sharedIssues: 'Shared Issues',
      uniqueToDesktop: 'Desktop Only',
      uniqueToMobile: 'Mobile Only',
    },
    es: {
      desktop: 'Escritorio',
      mobile: 'Movil',
      issues: 'problemas',
      critical: 'Critico',
      high: 'Alto',
      medium: 'Medio',
      low: 'Bajo',
      noIssues: 'Sin problemas encontrados',
      sharedIssues: 'Problemas Compartidos',
      uniqueToDesktop: 'Solo Escritorio',
      uniqueToMobile: 'Solo Movil',
    },
  };

  const t = i18n[locale as 'en' | 'es'] || i18n.en;

  // Sort findings by severity
  const sortedDesktop = useMemo(() => {
    const order = { critical: 0, high: 1, medium: 2, low: 3 };
    return [...desktopFindings].sort((a, b) => order[a.severity] - order[b.severity]);
  }, [desktopFindings]);

  const sortedMobile = useMemo(() => {
    const order = { critical: 0, high: 1, medium: 2, low: 3 };
    return [...mobileFindings].sort((a, b) => order[a.severity] - order[b.severity]);
  }, [mobileFindings]);

  return (
    <div className={styles.container}>
      {/* Screenshots Row */}
      {(desktopScreenshot || mobileScreenshot) && (
        <div className={styles.screenshotsRow}>
          <div className={styles.screenshotColumn}>
            {desktopScreenshot && (
              <div className={styles.screenshotWrapper}>
                <img
                  src={desktopScreenshot}
                  alt="Desktop view"
                  className={styles.screenshot}
                />
              </div>
            )}
          </div>
          <div className={styles.screenshotColumn}>
            {mobileScreenshot && (
              <div className={`${styles.screenshotWrapper} ${styles.mobileWrapper}`}>
                <img
                  src={mobileScreenshot}
                  alt="Mobile view"
                  className={styles.screenshot}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Side-by-Side Panels */}
      <div className={styles.compareGrid}>
        {/* Desktop Column */}
        <div className={styles.column}>
          <div className={styles.columnHeader}>
            <div className={styles.columnTitle}>
              <Monitor size={20} className={styles.columnIcon} />
              <span>{t.desktop}</span>
              <span className={styles.columnCount}>{desktopCounts.total} {t.issues}</span>
            </div>
            <div className={styles.severitySummary}>
              {desktopCounts.critical > 0 && (
                <span className={`${styles.severityPill} ${styles.critical}`}>
                  {desktopCounts.critical} {t.critical}
                </span>
              )}
              {desktopCounts.high > 0 && (
                <span className={`${styles.severityPill} ${styles.high}`}>
                  {desktopCounts.high} {t.high}
                </span>
              )}
              {desktopCounts.medium > 0 && (
                <span className={`${styles.severityPill} ${styles.medium}`}>
                  {desktopCounts.medium} {t.medium}
                </span>
              )}
              {desktopCounts.low > 0 && (
                <span className={`${styles.severityPill} ${styles.low}`}>
                  {desktopCounts.low} {t.low}
                </span>
              )}
            </div>
          </div>

          <div className={styles.findingsList}>
            {sortedDesktop.length === 0 ? (
              <div className={styles.emptyState}>{t.noIssues}</div>
            ) : (
              sortedDesktop.map((finding, index) => (
                <button
                  key={finding.principleId || index}
                  className={styles.findingItem}
                  onClick={() => onFindingClick?.(finding, 'desktop')}
                  type="button"
                >
                  <span className={`${styles.severityIndicator} ${styles[finding.severity]}`}>
                    {getSeverityIcon(finding.severity)}
                  </span>
                  <span className={styles.findingTitle}>
                    {finding.principleTitle || 'Issue'}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Divider */}
        <div className={styles.divider} />

        {/* Mobile Column */}
        <div className={styles.column}>
          <div className={styles.columnHeader}>
            <div className={styles.columnTitle}>
              <Smartphone size={20} className={styles.columnIcon} />
              <span>{t.mobile}</span>
              <span className={styles.columnCount}>{mobileCounts.total} {t.issues}</span>
            </div>
            <div className={styles.severitySummary}>
              {mobileCounts.critical > 0 && (
                <span className={`${styles.severityPill} ${styles.critical}`}>
                  {mobileCounts.critical} {t.critical}
                </span>
              )}
              {mobileCounts.high > 0 && (
                <span className={`${styles.severityPill} ${styles.high}`}>
                  {mobileCounts.high} {t.high}
                </span>
              )}
              {mobileCounts.medium > 0 && (
                <span className={`${styles.severityPill} ${styles.medium}`}>
                  {mobileCounts.medium} {t.medium}
                </span>
              )}
              {mobileCounts.low > 0 && (
                <span className={`${styles.severityPill} ${styles.low}`}>
                  {mobileCounts.low} {t.low}
                </span>
              )}
            </div>
          </div>

          <div className={styles.findingsList}>
            {sortedMobile.length === 0 ? (
              <div className={styles.emptyState}>{t.noIssues}</div>
            ) : (
              sortedMobile.map((finding, index) => (
                <button
                  key={finding.principleId || index}
                  className={styles.findingItem}
                  onClick={() => onFindingClick?.(finding, 'mobile')}
                  type="button"
                >
                  <span className={`${styles.severityIndicator} ${styles[finding.severity]}`}>
                    {getSeverityIcon(finding.severity)}
                  </span>
                  <span className={styles.findingTitle}>
                    {finding.principleTitle || 'Issue'}
                  </span>
                </button>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CompareView;
